import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { RffFieldProps } from '../form/FieldProps';
import { ClassNameProp } from '../form/types';
import { Label } from '../label/Label';
import { FilterType } from './types';
import { Menu } from '../menu/Menu';
import { MenuItem } from '../menu/MenuItem';
import { MenuDivider } from '../menu/MenuDivider';
import { useField } from 'react-final-form';
import { Icon } from '../icon/Icon';
import {
  mcCheck,
  mcDropDownFilled,
  mcNoValueSpecified,
} from '@mc/react-ui-icons';
import { Popup } from '../popover/Popup';
import { Text } from '../typography/Text';
import { ExtendedFilter } from './Filters';
import { DynamicDateValue } from '@mc/data-filter-react';

export const DYNAMIC_DATE_LABELS: Record<DynamicDateValue, string> = {
  [DynamicDateValue.YESTERDAY]: 'Včera',
  [DynamicDateValue.TODAY]: 'Dnes',
  [DynamicDateValue.TOMORROW]: 'Zítra',
  [DynamicDateValue.LTE_TODAY]: '<= dnes',
  [DynamicDateValue.GTE_TODAY]: '>= dnes',
  [DynamicDateValue.LT_TODAY]: '< dnes',
  [DynamicDateValue.GT_TODAY]: '> dnes',
  [DynamicDateValue.LAST_7_DAYS]: 'posledních 7 dní',
  [DynamicDateValue.NEXT_7_DAYS]: 'v příštích 7 dnech',
  [DynamicDateValue.LAST_WEEK]: 'minulý týden',
  [DynamicDateValue.THIS_WEEK]: 'tento týden',
  [DynamicDateValue.NEXT_WEEK]: 'příští týden',
  [DynamicDateValue.LAST_MONTH]: 'minulý měsíc',
  [DynamicDateValue.THIS_MONTH]: 'tento měsíc',
  [DynamicDateValue.NEXT_MONTH]: 'příští měsíc',
  [DynamicDateValue.LAST_QUARTER]: 'minulé čtvrtletí',
  [DynamicDateValue.THIS_QUARTER]: 'toto čtvrtletí',
  [DynamicDateValue.NEXT_QUARTER]: 'příští čtvrtletí',
  [DynamicDateValue.LAST_HALF_YEAR]: 'minulé pololetí',
  [DynamicDateValue.THIS_HALF_YEAR]: 'toto pololetí',
  [DynamicDateValue.NEXT_HALF_YEAR]: 'příští pololetí',
  [DynamicDateValue.LAST_YEAR]: 'minulý rok',
  [DynamicDateValue.THIS_YEAR]: 'tento rok',
  [DynamicDateValue.NEXT_YEAR]: 'příští rok',
};

const DYNAMIC_DATE_PRIMARY: DynamicDateValue[] = [
  DynamicDateValue.YESTERDAY,
  DynamicDateValue.TODAY,
  DynamicDateValue.TOMORROW,
  DynamicDateValue.LTE_TODAY,
  DynamicDateValue.GTE_TODAY,
];

const DYNAMIC_DATE_MORE: DynamicDateValue[] = [
  DynamicDateValue.LT_TODAY,
  DynamicDateValue.GT_TODAY,
  DynamicDateValue.LAST_7_DAYS,
  DynamicDateValue.NEXT_7_DAYS,
  DynamicDateValue.LAST_WEEK,
  DynamicDateValue.THIS_WEEK,
  DynamicDateValue.NEXT_WEEK,
  DynamicDateValue.LAST_MONTH,
  DynamicDateValue.THIS_MONTH,
  DynamicDateValue.NEXT_MONTH,
  DynamicDateValue.LAST_QUARTER,
  DynamicDateValue.THIS_QUARTER,
  DynamicDateValue.NEXT_QUARTER,
  DynamicDateValue.LAST_HALF_YEAR,
  DynamicDateValue.THIS_HALF_YEAR,
  DynamicDateValue.NEXT_HALF_YEAR,
  DynamicDateValue.LAST_YEAR,
  DynamicDateValue.THIS_YEAR,
  DynamicDateValue.NEXT_YEAR,
];

export const FilterItem: React.FC<
  React.PropsWithChildren<
    RffFieldProps &
      ClassNameProp & {
        label: React.ReactNode;
        disabled?: boolean;
        filterType?: FilterType;
        /**
         * Zda lze hodnotu filtrovat na vyplněnost/nevyplněnost, případně nerovná se apod.
         */
        extendedFilter?: ExtendedFilter;
      }
  >
> = ({ children, label, name, className, filterType, extendedFilter }) => {
  const showFilledFilterable =
    filterType === 'default' &&
    (extendedFilter?.filterByFilled ||
      extendedFilter?.filterByNot ||
      extendedFilter?.filterByDynamicDate);

  const cls = classNames(className, Classes.FILTER_GROUP, {
    [Classes.FILTER_FILLED_FILTERABLE]: showFilledFilterable,
  });

  const formField = useField(name);

  const isEmpty =
    filterType === 'default' ? formField.input.value?.emptyValue!! : false;
  const isNotEmpty =
    filterType === 'default' ? formField.input.value?.notEmptyValue!! : false;
  const isNot =
    filterType === 'default' ? formField.input.value?.notValue!! : false;
  const dynamicDateValue: DynamicDateValue | undefined =
    filterType === 'default'
      ? formField.input.value?.dynamicDateValue
      : undefined;

  const handleFilterByIsEmpty = () => {
    if (filterType === 'default') {
      formField.input.onChange({
        value: undefined,
        emptyValue: true,
        notEmptyValue: false,
        notValue: false,
      });
    }
  };

  const handleFilterByIsNotEmpty = () => {
    if (filterType === 'default') {
      formField.input.onChange({
        value: undefined,
        emptyValue: false,
        notEmptyValue: true,
        notValue: false,
      });
    }
  };

  const handleFilterByNotValue = () => {
    if (filterType === 'default') {
      formField.input.onChange({
        value: formField.input.value?.value,
        emptyValue: false,
        notEmptyValue: false,
        notValue: true,
      });
    }
  };

  const handleFilterByValue = () => {
    if (filterType === 'default') {
      formField.input.onChange({
        value: formField.input.value?.value,
        emptyValue: false,
        notEmptyValue: false,
        notValue: false,
      });
    }
  };

  const handleFilterByDynamicDate = (value: DynamicDateValue) => {
    if (filterType === 'default') {
      formField.input.onChange({
        value: undefined,
        emptyValue: false,
        notEmptyValue: false,
        notValue: false,
        dynamicDateValue: value,
      });
    }
  };

  return (
    <div className={cls}>
      {!showFilledFilterable && label && (
        <Label className={Classes.FILTER_LABEL}>{label}</Label>
      )}
      {showFilledFilterable && label && (
        <Label className={Classes.FILTER_LABEL}>
          <Popup
            nested
            trigger={
              <span className={Classes.FILTER_POPUP_LABEL}>
                {label}&nbsp;&nbsp;
                {isNot && (
                  <>
                    <Icon
                      className={Classes.FILTER_POPUP_LABEL_ALLWAYS_VISIBLE}
                      intent={'danger'}
                      icon={mcNoValueSpecified}
                    />
                    &nbsp;&nbsp;
                  </>
                )}
                <Icon icon={mcDropDownFilled} />
              </span>
            }
          >
            {(closePopup) => {
              return (
                <Menu>
                  <MenuItem
                    icon={
                      !isNotEmpty && !isEmpty && !isNot && !dynamicDateValue ? (
                        <Icon icon={mcCheck} />
                      ) : undefined
                    }
                    text={'Zadaná hodnota'}
                    onClick={() => {
                      closePopup();
                      handleFilterByValue();
                    }}
                  />
                  {extendedFilter?.filterByNot && (
                    <MenuItem
                      text={'Nerovná se zadané hodnotě'}
                      icon={isNot ? <Icon icon={mcCheck} /> : undefined}
                      onClick={() => {
                        closePopup();
                        handleFilterByNotValue();
                      }}
                    />
                  )}
                  {extendedFilter?.filterByFilled && (
                    <>
                      <MenuDivider />
                      <MenuItem
                        text={'Vyplněno'}
                        icon={isNotEmpty ? <Icon icon={mcCheck} /> : undefined}
                        onClick={() => {
                          closePopup();
                          handleFilterByIsNotEmpty();
                        }}
                      />
                      <MenuItem
                        text={'Nevyplněno'}
                        icon={isEmpty ? <Icon icon={mcCheck} /> : undefined}
                        onClick={() => {
                          closePopup();
                          handleFilterByIsEmpty();
                        }}
                      />
                    </>
                  )}
                  {extendedFilter?.filterByDynamicDate && (
                    <>
                      <MenuDivider />
                      {DYNAMIC_DATE_PRIMARY.map((val) => (
                        <MenuItem
                          key={val}
                          text={DYNAMIC_DATE_LABELS[val]}
                          icon={
                            dynamicDateValue === val ? (
                              <Icon icon={mcCheck} />
                            ) : undefined
                          }
                          onClick={() => {
                            closePopup();
                            handleFilterByDynamicDate(val);
                          }}
                        />
                      ))}
                      <MenuItem text={'Další hodnoty'}>
                        {DYNAMIC_DATE_MORE.map((val) => (
                          <MenuItem
                            key={val}
                            text={DYNAMIC_DATE_LABELS[val]}
                            icon={
                              dynamicDateValue === val ? (
                                <Icon icon={mcCheck} />
                              ) : undefined
                            }
                            onClick={() => {
                              closePopup();
                              handleFilterByDynamicDate(val);
                            }}
                          />
                        ))}
                      </MenuItem>
                    </>
                  )}
                </Menu>
              );
            }}
          </Popup>
        </Label>
      )}
      <div className={Classes.FILTER_FIELD}>
        {isEmpty && <Text type={'value'}>Nevyplněno</Text>}
        {isNotEmpty && <Text type={'value'}>Vyplněno</Text>}
        {dynamicDateValue && (
          <Text type={'value'}>{DYNAMIC_DATE_LABELS[dynamicDateValue]}</Text>
        )}
        {!isEmpty && !isNotEmpty && !dynamicDateValue && children}
      </div>
    </div>
  );
};

FilterItem.displayName = 'FilterItem';
